import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select, { type ClassNamesConfig, type MultiValue, type SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { api, type QueryParams } from '../../api/client';
import { AppIcon } from '../AppIcon';

export interface Option {
  value: string;
  label: string;
  /** Secondary text shown under the label in the menu (e.g. GSTIN, email). */
  hint?: string;
  /** Small logo before the label (apps). `null` shows the letter fallback. */
  icon?: string | null;
}

/** Server-side (AJAX) option source backed by a standard list endpoint. */
export interface RemoteSource {
  /** API collection, e.g. 'apps' → GET /api/apps?search=…&pageSize=20 */
  resource: string;
  params?: QueryParams;
  /** Field used as the option value (default 'id'). */
  valueKey?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label: (row: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hint?: (row: any) => string | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: (row: any) => string | null;
}

interface Props {
  value: string | null | undefined;
  onChange: (value: string) => void;
  options?: Option[];
  remote?: RemoteSource;
  placeholder?: string;
  /** Allow clearing back to '' (optional fields and filters). */
  clearable?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  inputId?: string;
  ariaLabel?: string;
  className?: string;
}

const classNames: ClassNamesConfig<Option, false> = {
  control: ({ isFocused, isDisabled }) =>
    clsx(
      'min-h-10 rounded-lg border bg-white px-2 text-sm shadow-sm',
      isFocused ? 'border-brand-500 ring-2 ring-brand-500/20' : 'border-slate-300',
      isDisabled && 'bg-slate-100 text-slate-500',
    ),
  valueContainer: () => 'gap-1 px-1',
  placeholder: () => 'text-slate-400',
  input: () => 'text-slate-900',
  singleValue: () => 'text-slate-900',
  indicatorsContainer: () => 'gap-0.5 text-slate-400',
  clearIndicator: () => 'p-1 hover:text-slate-600 cursor-pointer',
  dropdownIndicator: () => 'p-1 hover:text-slate-600',
  indicatorSeparator: () => 'hidden',
  menu: () => 'mt-1 rounded-lg border border-slate-200 bg-white text-sm shadow-lg overflow-hidden',
  menuList: () => 'py-1 max-h-64',
  option: ({ isFocused, isSelected }) =>
    clsx(
      'cursor-pointer px-3 py-2',
      isSelected
        ? 'bg-brand-600 text-white'
        : isFocused
          ? 'bg-slate-100 text-slate-900'
          : 'text-slate-700',
    ),
  noOptionsMessage: () => 'px-3 py-2 text-slate-500',
  loadingMessage: () => 'px-3 py-2 text-slate-500',
};

function formatOption(o: Option, { context }: { context: 'menu' | 'value' }) {
  const text =
    context === 'value' || !o.hint ? (
      o.label
    ) : (
      <div>
        <div>{o.label}</div>
        <div className="text-xs opacity-70">{o.hint}</div>
      </div>
    );
  if (o.icon === undefined) return text;
  return (
    <div className="flex items-center gap-2">
      <AppIcon src={o.icon} name={o.label} size={context === 'value' ? 18 : 24} />
      <div className="min-w-0">{text}</div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toOption(source: RemoteSource, row: any): Option {
  const inactive = row.status && row.status !== 'ACTIVE' ? ' (inactive)' : '';
  return {
    value: String(row[source.valueKey ?? 'id']),
    label: source.label(row) + inactive,
    hint: source.hint?.(row) ?? undefined,
    icon: source.icon ? source.icon(row) : undefined,
  };
}

/**
 * Searchable dropdown. With `remote` it queries the API as you type
 * (debounced) and resolves the label of an existing value by id, so it works
 * for large lists and for records that are no longer active.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  remote,
  placeholder = 'Search…',
  clearable = false,
  invalid,
  disabled,
  inputId,
  ariaLabel,
  className,
}: Props) {
  const common = {
    inputId,
    'aria-label': ariaLabel,
    'aria-invalid': invalid || undefined,
    placeholder,
    isClearable: clearable,
    isDisabled: disabled,
    unstyled: true,
    classNames: {
      ...classNames,
      control: (s: Parameters<NonNullable<typeof classNames.control>>[0]) =>
        clsx(classNames.control!(s), invalid && 'border-rose-400'),
    },
    formatOptionLabel: formatOption,
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuPosition: 'fixed' as const,
    styles: { menuPortal: (base: object) => ({ ...base, zIndex: 70 }) },
    className,
    onChange: (o: SingleValue<Option>) => onChange(o?.value ?? ''),
  };

  if (!remote) {
    const selected = options?.find((o) => o.value === (value ?? '')) ?? null;
    return <Select<Option, false> {...common} options={options} value={selected} />;
  }
  return <RemoteSelect {...common} remote={remote} value={value} />;
}

function RemoteSelect({
  remote,
  value,
  ...rest
}: Record<string, unknown> & { remote: RemoteSource; value: string | null | undefined }) {
  const [selected, setSelected] = useState<Option | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const paramsKey = JSON.stringify(remote.params ?? {});

  // Resolve the label of the current value (also for inactive records).
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.value === value) return;
    api
      .get(`/${remote.resource}/${encodeURIComponent(value)}`)
      .then((row) => !cancelled && setSelected(toOption(remote, row)))
      .catch(() => !cancelled && setSelected({ value, label: value }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, remote.resource]);

  const load = useMemo(
    () => (search: string) =>
      new Promise<Option[]>((resolve) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
          try {
            const res = await api.list(`/${remote.resource}`, {
              ...remote.params,
              search,
              pageSize: 25,
            });
            resolve(res.data.map((row) => toOption(remote, row)));
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remote.resource, paramsKey],
  );

  const onChange = rest.onChange as (o: SingleValue<Option>) => void;
  return (
    <AsyncSelect<Option, false>
      {...(rest as object)}
      key={paramsKey}
      cacheOptions
      defaultOptions
      loadOptions={load}
      value={selected}
      noOptionsMessage={({ inputValue }) => (inputValue ? 'No matches' : 'Type to search')}
      onChange={(o) => {
        setSelected(o);
        onChange(o);
      }}
    />
  );
}

/**
 * Searchable multi-select over a remote source (e.g. pick several accounts to
 * assign). Selected options are kept locally, so labels never need re-fetching.
 */
export function RemoteMultiSelect({
  remote,
  value,
  onChange,
  placeholder = 'Search…',
  inputId,
  ariaLabel,
}: {
  remote: RemoteSource;
  value: Option[];
  onChange: (value: Option[]) => void;
  placeholder?: string;
  inputId?: string;
  ariaLabel?: string;
}) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const paramsKey = JSON.stringify(remote.params ?? {});
  const load = useMemo(
    () => (search: string) =>
      new Promise<Option[]>((resolve) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
          try {
            const res = await api.list(`/${remote.resource}`, {
              ...remote.params,
              search,
              pageSize: 25,
            });
            resolve(res.data.map((row) => toOption(remote, row)));
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remote.resource, paramsKey],
  );
  const multiClassNames = classNames as unknown as ClassNamesConfig<Option, true>;
  return (
    <AsyncSelect<Option, true>
      key={paramsKey}
      isMulti
      cacheOptions
      defaultOptions
      loadOptions={load}
      value={value}
      onChange={(o: MultiValue<Option>) => onChange([...o])}
      inputId={inputId}
      aria-label={ariaLabel}
      placeholder={placeholder}
      unstyled
      closeMenuOnSelect
      classNames={{
        ...multiClassNames,
        multiValue: () => 'rounded bg-brand-50 text-brand-800 text-xs items-center',
        multiValueLabel: () => 'px-1.5 py-0.5',
        multiValueRemove: () => 'px-1 hover:bg-brand-100 rounded-r',
      }}
      formatOptionLabel={formatOption}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      menuPosition="fixed"
      styles={{ menuPortal: (base: object) => ({ ...base, zIndex: 70 }) }}
      noOptionsMessage={({ inputValue }) => (inputValue ? 'No matches' : 'No accounts available')}
    />
  );
}
